import { useState, useEffect, type ReactNode } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Tenant } from '@/lib/types';
import { useAuth } from '@/store/auth';
import { Icon, I, type IconKey } from '@/components/icons';
import { ProfileMenu } from '@/components/ProfileMenu';
import { useAlerts } from '@/lib/useAlerts';

/** 響應式斷點偵測：< 820px 視為行動 / 平板直立，側邊欄改抽屜。 */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const m = window.matchMedia(query);
    const handler = (): void => setMatches(m.matches);
    handler();
    m.addEventListener('change', handler);
    return () => m.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

type NavItem = { to: string; label: string; icon: IconKey; badge?: string; tier?: 'manager' | 'owner' };
type NavGroup = { label: string; items: NavItem[] };

const GROUPS: NavGroup[] = [
  {
    label: '🏥 分店現場',
    items: [
      { to: '/dashboard', label: '今日調度看板', icon: 'home' },
      { to: '/quickadd', label: '30 秒舊客補登', icon: 'zap' },
      { to: '/customers', label: '顧客主頁', icon: 'user' },
      { to: '/booking', label: '三維預約引擎', icon: 'cal' },
      { to: '/billing', label: '儲值 / 堂票核銷', icon: 'wallet' },
      { to: '/checkout', label: '一鍵結帳', icon: 'zap' },
      { to: '/clinic-tools', label: '候補 / 報價 / 會員', icon: 'package' },
      { to: '/scheduling', label: '排班系統', icon: 'clock' },
      { to: '/compliance', label: '合規與風險', icon: 'shield' },
      { to: '/photos', label: '顧客影像庫', icon: 'eye' },
      { to: '/line', label: 'LINE OA 對話', icon: 'line' },
      { to: '/line-funnel', label: 'LINE 銷售漏斗', icon: 'trend' },
    ],
  },
  {
    label: '🏢 品牌 / 租戶',
    items: [
      { to: '/catalog', label: '方案上架管理', icon: 'package' },
      { to: '/inventory', label: '耗材庫存管理', icon: 'layers' },
      { to: '/suppliers', label: '供應商 / 領取', icon: 'building' },
      { to: '/staff', label: '員工 / 權限', icon: 'users', tier: 'owner' },
      { to: '/settings', label: '白牌 / 租戶設定', icon: 'settings', tier: 'owner' },
      { to: '/reports', label: '財務雙軌報表', icon: 'chart', tier: 'manager' },
      { to: '/daily-close', label: '日結 / 現金對帳', icon: 'wallet', tier: 'manager' },
      { to: '/commission', label: '員工業績獎金', icon: 'wallet', tier: 'manager' },
      { to: '/aftercare', label: '術後關懷排程', icon: 'bell' },
    ],
  },
  {
    label: '📊 經營與分析',
    items: [
      { to: '/growth', label: '增長與轉介', icon: 'trend' },
      { to: '/recall', label: '到期喚回名單', icon: 'bell' },
      { to: '/funnel', label: '諮詢轉換漏斗', icon: 'chart', tier: 'manager' },
      { to: '/utilization', label: '產能利用率', icon: 'clock', tier: 'manager' },
      { to: '/branch-pnl', label: '分店損益', icon: 'chart', tier: 'manager' },
      { to: '/advanced', label: '進階營運模組', icon: 'sparkle', tier: 'owner' },
      { to: '/audit', label: '稽核日誌', icon: 'shield', tier: 'manager' },
    ],
  },
];

function TopBar({
  name,
  role,
  onLogout,
  isMobile,
  onMenu,
}: {
  name: string;
  role: string;
  onLogout: () => void;
  isMobile: boolean;
  onMenu: () => void;
}) {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const tenant = useQuery({ queryKey: ['tenant'], queryFn: () => api.get<Tenant>('/tenant') });
  const brand = tenant.data?.brand ?? '漂亮 CRM';
  const runSearch = () => {
    const q = term.trim();
    if (q) navigate(`/search?q=${encodeURIComponent(q)}`);
  };
  return (
    <div
      style={{
        height: 48,
        background: 'var(--surface)',
        borderBottom: '1px solid var(--line)',
        display: 'flex',
        alignItems: 'center',
        padding: isMobile ? '0 12px' : '0 16px',
        gap: isMobile ? 8 : 12,
        flexShrink: 0,
      }}
    >
      {isMobile && (
        <button
          type="button"
          aria-label="選單"
          onClick={onMenu}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 34,
            height: 34,
            borderRadius: 8,
            border: '1px solid var(--line)',
            background: 'var(--surface)',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <Icon d={I.menu} size={18} style={{ color: 'var(--ink-2)' }} />
        </button>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '4px 10px 4px 4px',
          background: 'var(--primary-tint)',
          border: '1px solid var(--primary-soft)',
          borderRadius: 999,
          color: 'var(--primary-deep)',
          fontSize: 12,
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        <span
          style={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: 'var(--primary)',
            color: '#fff',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {brand.slice(0, 1)}
        </span>
        <span>{brand}</span>
      </div>

      <div style={{ flex: 1 }} />

      {isMobile ? (
        <button
          type="button"
          aria-label="搜尋"
          onClick={() => navigate('/search')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 34,
            height: 34,
            borderRadius: 8,
            border: '1px solid var(--line)',
            background: 'var(--surface)',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <Icon d={I.search} size={16} style={{ color: 'var(--ink-2)' }} />
        </button>
      ) : (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '5px 10px',
            background: 'var(--surface-2)',
            border: '1px solid var(--line-soft)',
            borderRadius: 8,
            minWidth: 260,
            color: 'var(--ink-3)',
          }}
        >
          <Icon d={I.search} size={14} />
          <input
            placeholder="搜尋顧客 / 電話（Enter 查詢）"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') runSearch();
            }}
            style={{
              flex: 1,
              border: 0,
              background: 'transparent',
              outline: 'none',
              fontSize: 12,
              color: 'var(--ink)',
            }}
          />
          <button
            type="button"
            onClick={runSearch}
            className="mono"
            style={{ fontSize: 10, color: 'var(--ink-4)', background: 'transparent', border: 0, cursor: 'pointer' }}
          >
            ⏎
          </button>
        </div>
      )}

      <NotificationBell />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          paddingLeft: 8,
          borderLeft: '1px solid var(--line)',
        }}
      >
        <ProfileMenu name={name} role={role} onLogout={onLogout} />
      </div>
    </div>
  );
}

function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { items, total } = useAlerts();
  const tone = (s: string) => (s === 'danger' ? 'var(--danger)' : s === 'warn' ? 'var(--warn)' : 'var(--ink-3)');

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        aria-label="通知"
        onClick={() => setOpen((v) => !v)}
        style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 8, border: '1px solid var(--line)', background: 'var(--surface)', cursor: 'pointer' }}
      >
        <Icon d={I.bell} size={16} style={{ color: 'var(--ink-2)' }} />
        {total > 0 && (
          <span style={{ position: 'absolute', top: -5, right: -5, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 99, background: 'var(--danger)', color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }} className="num">
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setOpen(false)} />
          <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 320, maxHeight: 420, overflow: 'auto', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.16)', zIndex: 50 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderBottom: '1px solid var(--line)' }}>
              <b style={{ fontSize: 13 }}>通知 {total > 0 ? `(${total})` : ''}</b>
              <button type="button" onClick={() => { setOpen(false); navigate('/notifications'); }} style={{ fontSize: 11, color: 'var(--primary)', background: 'transparent', border: 0, cursor: 'pointer' }}>全部查看</button>
            </div>
            {items.slice(0, 8).map((a) => (
              <button key={a.id} type="button" onClick={() => { setOpen(false); navigate(a.to); }} style={{ display: 'flex', gap: 8, width: '100%', textAlign: 'left', padding: '9px 12px', border: 0, borderBottom: '1px solid var(--line-soft)', background: 'transparent', cursor: 'pointer' }}>
                <span style={{ width: 7, height: 7, borderRadius: 99, background: tone(a.severity), marginTop: 5, flexShrink: 0 }} />
                <span style={{ flex: 1 }}>
                  <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--ink)' }}>{a.title}</span>
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--ink-3)' }}>{a.detail}</span>
                </span>
              </button>
            ))}
            {total === 0 && <div style={{ padding: 20, textAlign: 'center', fontSize: 12, color: 'var(--ink-4)' }}>沒有待處理通知 🎉</div>}
          </div>
        </>
      )}
    </div>
  );
}

function SideNav({ onLogout, isManager, isOwner }: { onLogout: () => void; isManager: boolean; isOwner: boolean }) {
  const groups = GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((it) => !it.tier || (it.tier === 'owner' ? isOwner : isManager)),
  })).filter((g) => g.items.length > 0);
  return (
    <div
      style={{
        width: 220,
        height: '100%',
        background: 'var(--surface-2)',
        borderRight: '1px solid var(--line)',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
      }}
    >
      <div style={{ padding: '16px 16px 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: 'var(--primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontWeight: 700,
            fontSize: 14,
            boxShadow: 'inset 0 -2px 4px rgba(0,0,0,0.08)',
          }}
        >
          漂
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
          <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: 0.5 }}>漂亮 CRM</span>
          <span style={{ fontSize: 10, color: 'var(--ink-3)' }}>v1.0.0 · 多租戶</span>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '4px 8px 16px' }}>
        {groups.map((g) => (
          <div key={g.label} style={{ marginTop: 12 }}>
            <div
              style={{
                padding: '6px 10px 4px',
                fontSize: 10,
                color: 'var(--ink-3)',
                letterSpacing: 0.6,
                fontWeight: 600,
              }}
            >
              {g.label}
            </div>
            {g.items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                style={({ isActive }) => ({
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '7px 10px',
                  borderRadius: 8,
                  fontSize: 12.5,
                  textDecoration: 'none',
                  background: isActive ? 'var(--primary-tint)' : 'transparent',
                  color: isActive ? 'var(--primary-deep)' : 'var(--ink-2)',
                  fontWeight: isActive ? 600 : 500,
                  margin: '1px 0',
                })}
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      d={I[it.icon]}
                      size={15}
                      style={{
                        color: isActive ? 'var(--primary)' : 'var(--ink-3)',
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ flex: 1 }}>{it.label}</span>
                    {it.badge && (
                      <span
                        style={{
                          fontSize: 10,
                          padding: '1px 6px',
                          borderRadius: 999,
                          background: isActive ? 'var(--primary)' : 'var(--ink-4)',
                          color: '#fff',
                          fontWeight: 600,
                          minWidth: 18,
                          textAlign: 'center',
                        }}
                      >
                        {it.badge}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </div>

      <div
        style={{
          padding: '10px 14px',
          borderTop: '1px solid var(--line)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <button
          className="btn btn-ghost"
          style={{ flex: 1, padding: '6px 10px', fontSize: 12 }}
          onClick={onLogout}
        >
          <Icon d={I.arrow} size={13} /> 登出
        </button>
      </div>
    </div>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const isMobile = useMediaQuery('(max-width: 820px)');
  const [navOpen, setNavOpen] = useState(false);
  const loc = useLocation();

  // 換頁時自動關閉抽屜
  useEffect(() => {
    setNavOpen(false);
  }, [loc.pathname]);

  const nav = (
    <SideNav
      onLogout={() => void logout()}
      isManager={user?.role === 'TENANT_ADMIN' || user?.role === 'STORE_MANAGER'}
      isOwner={user?.role === 'TENANT_ADMIN'}
    />
  );

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <TopBar
        name={user?.name ?? ''}
        role={user?.role ?? ''}
        onLogout={() => void logout()}
        isMobile={isMobile}
        onMenu={() => setNavOpen(true)}
      />
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0, position: 'relative' }}>
        {/* 桌面：固定側邊欄；行動：抽屜 */}
        {!isMobile && nav}
        {isMobile && navOpen && (
          <>
            <div
              onClick={() => setNavOpen(false)}
              style={{ position: 'fixed', inset: 0, top: 48, background: 'rgba(0,0,0,0.42)', zIndex: 60 }}
            />
            <div
              style={{
                position: 'fixed',
                top: 48,
                left: 0,
                bottom: 0,
                zIndex: 70,
                boxShadow: '2px 0 18px rgba(0,0,0,0.22)',
              }}
            >
              {nav}
            </div>
          </>
        )}
        <main className="app-main" style={{ flex: 1, overflow: 'auto', padding: isMobile ? 14 : 24, minWidth: 0 }}>
          {children}
        </main>
      </div>
    </div>
  );
}
